import { useLazyGetBeltMisalignmentQuery, useLazyGetRockSizeQuery } from '@/store/api/sensorsApi';
import { RootState } from '@/store/store';
import { Asset } from '@/types/shared/asset.types';
import { useCallback, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { format } from 'date-fns';
import { DIAGNOSTICS_FROM_DATE_FORMAT, DIAGNOSTICS_TO_DATE_FORMAT } from '@/const';
import { useQueryNavigation } from '@/hooks/shared';

interface useFetchBeltMisalignmentProps {
  asset: Asset;
}

export const useFetchVisualAIData = ({ asset }: useFetchBeltMisalignmentProps) => {
  const { siteId, machineId } = useParams();

  const {
    dateRange: { from, to },
    selectedComponentObj,
  } = useSelector((state: RootState) => state.assetsStudio);

  const [
    fetchBeltMisalignmentData,
    {
      data: beltMisalignmentData,
      isLoading: isLoadingBeltMisalignment,
      isFetching: isFetchingBeltMisalignment,
      isError: isErrorBeltMisalignment,
    },
  ] = useLazyGetBeltMisalignmentQuery({});

  const [
    fetchRockSizeData,
    { data: rockSizeData, isLoading: isLoadingRockSize, isFetching: isFetchingRockSize, isError: isErrorRockSize },
  ] = useLazyGetRockSizeQuery({});

  const queryParams: Record<string, string | undefined> = useMemo(() => {
    return {
      site_id: siteId as string,
      asset_id: asset?.asset_id,
      asset_name: asset?.asset_name,
      component_id: selectedComponentObj?.component_id,
      component_name: selectedComponentObj?.component_name,
      to_date: to ? format(new Date(to), DIAGNOSTICS_TO_DATE_FORMAT) : undefined,
      from_date: from ? format(new Date(from), DIAGNOSTICS_FROM_DATE_FORMAT) : undefined,
    };
  }, [siteId, asset, selectedComponentObj, to, from]);

  const fetchVisualAIData = useCallback(() => {
    fetchRockSizeData({ ...queryParams }, true);
    fetchBeltMisalignmentData({ ...queryParams }, true);
  }, [fetchRockSizeData, fetchBeltMisalignmentData, queryParams]);

  useEffect(() => {
    if (selectedComponentObj) fetchVisualAIData();
  }, [fetchVisualAIData, machineId, from, to]);

  return {
    isLoading: isLoadingBeltMisalignment || isLoadingRockSize,
    isFetching: isFetchingBeltMisalignment || isFetchingRockSize,
    isError: isErrorBeltMisalignment || isErrorRockSize,
    beltMisalignmentData,
    rockSizeData,
  };
};
